import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

/**
 * PrintColumnsDialog
 * - prefsKey: unique key stored on user.print_column_prefs
 * - columns: array of { key, label, always?: boolean }
 * - buildUrl(selectedKeys) → returns URL to open in a new tab
 */
export default function PrintColumnsDialog({ prefsKey, columns, buildUrl, testIdPrefix = "print" }) {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!open) return;
    const saved = user?.print_column_prefs?.[prefsKey];
    const initial = Array.isArray(saved) && saved.length > 0
      ? saved
      : columns.map((c) => c.key);
    // ensure "always" columns are always included
    const enforced = new Set(initial);
    columns.forEach((c) => { if (c.always) enforced.add(c.key); });
    setSelected([...enforced]);
  }, [open, user, prefsKey, columns]);

  const toggle = (key) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const doPrint = async () => {
    const ordered = columns.filter((c) => c.always || selected.includes(c.key)).map((c) => c.key);
    try {
      const { data } = await api.patch("/auth/me/print-prefs", { key: prefsKey, columns: ordered });
      setUser(data);
    } catch (e) {
      // Not fatal — still print
      toast.error(formatApiError(e));
    }
    window.open(buildUrl(ordered), "_blank");
    setOpen(false);
  };

  return (
    <>
      <Button
        data-testid={`${testIdPrefix}-open`}
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-emerald-900 text-emerald-900 hover:bg-emerald-50"
      >
        <Printer className="h-4 w-4 ml-2" /> طباعة
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-right">تخصيص أعمدة الطباعة</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">اختر الأعمدة التي ستظهر في المطبوعة. سيتم حفظ اختيارك لهذا الجهاز/الحساب تلقائياً.</p>
          <div className="grid grid-cols-2 gap-2 py-2">
            {columns.map((c) => {
              const checked = c.always || selected.includes(c.key);
              return (
                <label
                  key={c.key}
                  data-testid={`${testIdPrefix}-col-${c.key}`}
                  className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                    checked ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200 hover:bg-slate-50"
                  } ${c.always ? "opacity-70" : ""}`}
                >
                  <Checkbox
                    checked={checked}
                    disabled={c.always}
                    onCheckedChange={() => toggle(c.key)}
                  />
                  <span className="text-sm text-emerald-950 font-medium">{c.label}</span>
                  {c.always && <span className="mr-auto text-[10px] text-slate-400">دائماً</span>}
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button
              data-testid={`${testIdPrefix}-confirm`}
              onClick={doPrint}
              className="bg-emerald-900 hover:bg-emerald-800 text-white"
            >
              <Printer className="h-4 w-4 ml-2" /> طباعة الآن
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
